<template>
    <div :style="{ display: inline ? 'inline' : 'block' }">
        <kgr-loading
            v-if="loading"
            inline
        />
        <select
            v-else
            ref="select"
            v-model="selectionId"
            class="kgr-select"
            :style="{ width: inline ? 'initial': '' }"
            :disabled="disabled"
            @change="update()"
        >
            <option
                v-if="!noNullOption"
                :value="null"
                class="kgr-select__option"
            >
                {{ $t(current.nullDefault) }}
            </option>
            <option
                v-for="option in options"
                :value="getOptionValue(option)"
                :disabled="option.disabled"
                class="kgr-select__option"
            >
                {{ displayOption(option) }}
            </option>
        </select>
        <div
            v-if="error != null"
            class="kgr-color kgr-color--error"
        >
            {{ error }}<br />
        </div>
    </div>
</template>

<script>
import Loading from '../../Loading';

export default {
    compatConfig: {
        MODE: 3,
    },

    components: {
        'kgr-loading': Loading,
    },

    props: {
        modelValue: {
            type: [Object, String, Number],
            required: false,
        },
        value: {
            type: [Object, String, Number, null],
            required: false,
        },
        defaultText: {
            type: String,
            required: false,
        },
        options: {
            type: Array,
            required: true,
        },
        optionKey: {
            type: String,
            required: false,
            default: null,
        },
        display: {
            type: String,
            required: false,
        },
        noNullOption: {
            type: Boolean,
            required: false,
            default: false
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        inline: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        translateValues: {
            type: Boolean,
            required: false,
            default: true,
        },
        error: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['update:modelValue', 'input', 'post-input'],

    data() {
        const selection = this.modelValue ?? this.value ?? null;
        return {
            selection,
            selectionId: null,

            current: {
                nullDefault: '',
                optionsType: 'object',
                optionKey: this.optionKey,
            }
        }
    },

    watch: {
        modelValue: {
            handler(value) { return this.handleSelectionChange(value) },
            deep: true,
        },
        value: {
            handler(value) { return this.handleSelectionChange(value) },
            deep: true,
        },
        options: {
            handler() {
                const firstOption = this.options?.[0];
                this.current.optionsType = firstOption instanceof Object ? 'object' : 'primitive';
                this.current.optionKey = this.detectoptionKey(firstOption)
                this.handleSelectionChange(this.modelValue ?? this.value ?? null)

                if (this.selectionId !== null) {
                    const selectedOption = this.findSelectionById(this.selectionId);
                    if (!selectedOption) {
                        this.selection = null;
                        this.update();
                        return;
                    }

                    this.selection = selectedOption;
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.current.nullDefault = this.defaultText ? this.defaultText : 'pleaseChooseAnOption';
        this.current.optionKey = this.optionKey || this.detectoptionKey(this.options?.[0])
        this.handleSelectionChange(this.selection)
    },
    methods: {
        detectoptionKey(option) {
            if (this.optionKey) {
                return this.optionKey;
            }

            if (!option || !(option instanceof Object)) {
                return null
            }
            return Object.keys(option).find(key => ['optionKey', 'id', 'key', 'value', 'name', 'title'].includes(key)) || null;
        },

        handleSelectionChange(newValue) {
            if (!(newValue instanceof Object)) {
                this.selectionId = newValue || null;
                this.selection = this.findSelectionById(this.selectionId);
                return;
            }

            if (this.current.optionsType === 'object' && !this.current.optionKey) {
                this.current.optionKey = this.detectoptionKey(newValue)
            }

            if (this.current.optionsType !== 'object' || !this.current.optionKey) {
                this.selectionId = newValue || null;
                this.selection = newValue || null;
                return;
            }

            this.selectionId = (this.current.optionKey ? newValue[this.current.optionKey] : newValue) || null;
            this.selection = this.findSelectionById(this.selectionId);
        },

        findSelectionById(id) {
            if (this.current.optionsType === 'object' && this.current.optionKey) {
                return this.options.find(option => option[this.current.optionKey] === id) || null;
            } else {
                return this.options.includes(id) ? id : null;
            }
        },

        update() {
            this.selection = this.findSelectionById(this.selectionId);

            this.$emit('update:modelValue', this.selection);
            // support legacy dependency on 'input' event
            this.$emit('input', this.selection);
            this.$nextTick(() => {
                this.$emit('post-input', this.selection);
            });
        },

        getOptionValue(option) {
            if (this.current.optionKey && option instanceof Object) {
                return option[this.current.optionKey] || null;
            }
            return option || null;
        },

        displayOption(option) {
            if (this.display) {
                return typeof option[this.display] === 'function' ? option[this.display]() : option[this.display];
            }

            if (this.translateValues) {
                return this.$t('' + option);
            }

            if (this.current.optionKey) {
                return option[this.current.optionKey];
            }

            return option;
        },
    },
}
</script>
